import React from 'react';
import { Box } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import {
  AcUnit as AcUnitIcon,
  CheckCircle as CheckCircleIcon,
  CloudOff as CloudOffIcon,
  Event as EventIcon,
  HourglassEmpty as HourglassIcon,
  RemoveCircleOutline as NoLimitIcon,
  Schedule as ScheduleIcon,
  Sensors as SensorsIcon,
  StopCircle as StopCircleIcon,
  Thermostat as ThermostatIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import type { LocationAlert } from './mobileDashboardModel';

/** Tinted fill and dark same-family text (the StatusBadge palette), plus
 *  blue for scheduled and awaiting. */
export const PILL_TONES = {
  error: { bg: '#FDECEA', fg: '#A32D2D' },
  warning: { bg: '#FAEEDA', fg: '#9a6a00' },
  good: { bg: '#E1F5EE', fg: '#0f6e56' },
  neutral: { bg: '#EDEDED', fg: '#5f5f5f' },
  info: { bg: '#E6F1FB', fg: '#185FA5' },
} as const;

export type PillTone = keyof typeof PILL_TONES;

interface AlertLook {
  tone: PillTone;
  Icon: React.ComponentType<SvgIconProps>;
}

/** Each state's colour and shape, so it never rests on colour alone. */
export const ALERT_LOOKS: Record<LocationAlert, AlertLook> = {
  over_limit: { tone: 'error', Icon: ThermostatIcon },
  under_limit: { tone: 'error', Icon: AcUnitIcon },
  offline: { tone: 'error', Icon: CloudOffIcon },
  delayed: { tone: 'warning', Icon: ScheduleIcon },
  sensor: { tone: 'warning', Icon: WarningIcon },
  ok: { tone: 'good', Icon: CheckCircleIcon },
  no_limits: { tone: 'neutral', Icon: NoLimitIcon },
  // Reporting fine (a site or humidity channel): no limit was checked, so
  // not the "OK" check mark.
  reporting: { tone: 'good', Icon: SensorsIcon },
  awaiting: { tone: 'info', Icon: HourglassIcon },
  scheduled: { tone: 'info', Icon: EventIcon },
  ended: { tone: 'neutral', Icon: StopCircleIcon },
};

/** States the phone does not show: a location with no limit to judge, and
 *  one within its limits ("OK"). Desktop never says either, and neither is
 *  anything to act on; the card shows just its readings. */
const SHOWS_NO_STATE: ReadonlySet<LocationAlert> = new Set<LocationAlert>(['no_limits', 'ok']);

/** The state's icon alone, in its text colour (pickers, compact rows); an
 *  empty slot of the same size when the state is not shown. */
export const AlertIcon: React.FC<{ alert: LocationAlert; size?: number }> = ({ alert, size = 18 }) => {
  if (SHOWS_NO_STATE.has(alert)) return <Box aria-hidden sx={{ width: size, flexShrink: 0 }} />;
  const { tone, Icon } = ALERT_LOOKS[alert];
  return <Icon aria-hidden sx={{ fontSize: size, color: PILL_TONES[tone].fg, flexShrink: 0 }} />;
};

interface StatusPillProps {
  alert: LocationAlert;
  /** The word ("Over limit", "Starts in 2 h"). */
  label: string;
}

/** A location's state as a pill: icon and word, always both (none at all
 *  for a state the phone does not show). */
export const StatusPill: React.FC<StatusPillProps> = ({ alert, label }) => {
  if (SHOWS_NO_STATE.has(alert)) return null;
  const { tone, Icon } = ALERT_LOOKS[alert];
  const { bg, fg } = PILL_TONES[tone];
  return (
    <Box
      component="span"
      data-alert={alert}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        minHeight: 28,
        px: 1,
        borderRadius: '999px',
        bgcolor: bg,
        color: fg,
        flexShrink: 0,
        maxWidth: '100%',
      }}
    >
      <Icon aria-hidden sx={{ fontSize: 16, flexShrink: 0 }} />
      <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        {label}
      </Box>
    </Box>
  );
};

export default StatusPill;
